"use client"

import { useState, useEffect, useRef } from "react"
import { Button } from "../../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Badge } from "../../ui/badge"
import { Input } from "../../ui/input"
import { Textarea } from "../../ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible"
import { Clock, Calendar, Plus, Edit, Trash2, Info, ChevronDown, Send, X, MessageSquare } from "lucide-react"
import { BulkEntryModal } from "./bulk-entry-modal"
import { WeekSelector } from "./week-selector"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog"
import { CommentsModalEmp } from "../../modals/comments-emp"
import { toast } from "../../hooks/use-toast"
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip"
import { decodeHtmlEntities } from "../../lib/utils"

interface TimesheetEntry {
  lineID: number
  id: string
  engagement: string
  task: string
  hours: number
  minutes: number
  comments: string
  date: string
}

const createEmptyEntry = () => ({
  engagement: "",
  task: "",
  hours: 0,
  minutes: 0,
  comments: "",
})

interface TimesheetPageProps {
  sidebarOpen?: boolean
}

const TEXT_TRUNCATE_LIMIT = 28

export function TimesheetPage({ sidebarOpen = false }: TimesheetPageProps) {
  const [showNag1, setShowNag1] = useState(true)
  const [showNag2, setShowNag2] = useState(true)
  const [showPopup, setShowPopup] = useState(false)

  const [addEntryErrors, setAddEntryErrors] = useState({
    engagement: "",
    task: "",
    hours: "",
  });
  
  const [currentWeek, setCurrentWeek] = useState("")
  const [selectedDate, setSelectedDate] = useState("Monday, August 18")
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false)
  const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false)
  const [isAddingEntry, setIsAddingEntry] = useState(false)
  const [editingEntry, setEditingEntry] = useState<string | null>(null)
  const [weekDays, setWeekDays] = useState([
    { day: "Mon", date: "18", full: "Monday, August 18", status: "filled", isWeekend: false, iso: "" },
    { day: "Tue", date: "19", full: "Tuesday, August 19", status: "filled", isWeekend: false, iso: "" },
    { day: "Wed", date: "20", full: "Wednesday, August 20", status: "filled", isWeekend: false, iso: "" },
    { day: "Thu", date: "21", full: "Thursday, August 21", status: "filled", isWeekend: false, iso: "" },
    { day: "Fri", date: "22", full: "Friday, August 22", status: "filled", isWeekend: false, iso: "" },
    { day: "Sat", date: "23", full: "Saturday, August 23", status: "optional", isWeekend: true, iso: "" },
    { day: "Sun", date: "24", full: "Sunday, August 24", status: "optional", isWeekend: true, iso: "" },
  ])

  const [newEntry, setNewEntry] = useState(createEmptyEntry)
  const [editEntryDraft, setEditEntryDraft] = useState(createEmptyEntry())

  const [engagementOptions, setEngagementOptions] = useState<{ engagementID: number; title: string }[]>([])
  const [taskOptions, setTaskOptions] = useState<{ taskID: number; taskName: string }[]>([])

  const [timesheetId, setTimesheetId] = useState<number | null>(null)
  const [isSubmitOpen, setIsSubmitOpen] = useState(false)
  const [submitComment, setSubmitComment] = useState("")
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [warnMessage, setWarnMessage] = useState<string>("")
  const [showWarnToast, setShowWarnToast] = useState(false)
  const [originalEntry, setOriginalEntry] = useState<TimesheetEntry | null>(null)
  const [isViewingSpecificWeek, setIsViewingSpecificWeek] = useState(false)
  const [selectedWeekTimesheetId, setSelectedWeekTimesheetId] = useState<number | null>(null)
  const [timesheetStatus, setTimesheetStatus] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isCommentsDisabled, setIsCommentsDisabled] = useState(false)

  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const [commentsData, setCommentsData] = useState<any[]>([])

  const [v1CurrentStartISO, setV1CurrentStartISO] = useState<string | null>(null)
  const [v1CurrentEndISO, setV1CurrentEndISO] = useState<string | null>(null)
  const [rejectedCount, setRejectedCount] = useState<number>(0)
  const [rejectedRanges, setRejectedRanges] = useState<{ timesheetID: number; startDate: string; endDate: string }[]>([])

  const handleNotify = () => {
    setShowPopup(true)
    setTimeout(() => {
      setShowPopup(false)
    }, 3000)
  }

  const [entries, setEntries] = useState<TimesheetEntry[]>([])

  const getCurrentWeekDates = () => {
    return weekDays.map((day) => day.full)
  }

  const getCurrentWeekEntries = () => {
    const currentWeekDates = getCurrentWeekDates()
    return entries.filter((entry) => currentWeekDates.includes(entry.date))
  }

  const currentDayEntries = getCurrentWeekEntries().filter((entry) => entry.date === selectedDate)
  const totalHours = currentDayEntries.reduce((sum, entry) => sum + entry.hours + entry.minutes / 60, 0)

  const weekTotalMinutesRaw = getCurrentWeekEntries().reduce((sum, entry) => sum + entry.hours * 60 + entry.minutes, 0)
  const weekTotalHours = weekTotalMinutesRaw / 60
  
  const formatWeeklyHours = (totalHours: number) => {
    const hours = Math.floor(totalHours)
    let minutes = Math.round((totalHours - hours) * 60)
    // Convert 60 minutes to 1 hour
    if (minutes >= 60) {
      const additionalHours = Math.floor(minutes / 60)
      const finalHours = hours + additionalHours
      minutes = minutes % 60
      return `${finalHours}h ${minutes}m`
    }
    return `${hours}h ${minutes}m`
  }

  const getDayHours = (dayFull: string) => {
    const dayEntries = getCurrentWeekEntries().filter((entry) => entry.date === dayFull)
    return dayEntries.reduce((sum, entry) => sum + entry.hours + entry.minutes / 60, 0)
  }

  const getDayTotalsMinutes = (dayFull: string) => {
    const dayEntries = getCurrentWeekEntries().filter((entry) => entry.date === dayFull)
    return dayEntries.reduce((sum, e) => sum + e.hours * 60 + e.minutes, 0)
  }

  const getWeekTotalsMinutes = () => {
    return getCurrentWeekEntries().reduce((sum, e) => sum + e.hours * 60 + e.minutes, 0)
  }

  const weekdaysFilled = weekDays
    .filter((day) => !day.isWeekend)
    .every((day) => {
      return getDayHours(day.full) >= 8
    })

  const today = new Date()

  const updateWeekDays = (weekString: string, preserveSelectedDate: boolean = false) => {
    // Parse week string like "Jul 28 - Aug 03, 2025" (start is Monday)
    const [range, yearStr] = weekString.split(", ")
    const [startStr] = range.split(" - ")
    const [month, day] = startStr.split(" ")

    const monthMap: { [key: string]: number } = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    }

    const year = Number.parseInt(yearStr)
    const monday = new Date(year, monthMap[month], Number.parseInt(day))

    const newWeekDays = [] as any[]
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    const fullDayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ]

    const toISODate = (date: Date) => {
      const y = date.getFullYear()
      const m = (date.getMonth() + 1).toString().padStart(2, "0")
      const d = date.getDate().toString().padStart(2, "0")
      return `${y}-${m}-${d}`
    }

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(monday)
      currentDate.setDate(monday.getDate() + i)

      const isWeekend = i >= 5
      const isPastOrToday = currentDate <= today

      newWeekDays.push({
        day: dayNames[i],
        date: currentDate.getDate().toString().padStart(2, "0"),
        full: `${fullDayNames[i]}, ${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}`,
        status: isPastOrToday ? (isWeekend ? "optional" : "filled") : "future",
        isWeekend,
        iso: toISODate(currentDate),
      })
    }

    setWeekDays(newWeekDays)
    
    // Only change selected date if not preserving, or if current date not in new week
    if (!preserveSelectedDate) {
      setSelectedDate(newWeekDays[0].full)
    } else {
      // Check if current selectedDate exists in new week
      const dateStillValid = newWeekDays.some(d => d.full === selectedDate)
      if (!dateStillValid) {
        setSelectedDate(newWeekDays[0].full)
      }
      // else keep the current selectedDate
    }
  }

  useEffect(() => {
    if (!currentWeek) return
    updateWeekDays(currentWeek)
  }, [currentWeek])

  const toISODate = (date: Date) => {
    const y = date.getFullYear()
    const m = (date.getMonth() + 1).toString().padStart(2, "0")
    const d = date.getDate().toString().padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  const loadWeekEntries = async (monday: Date) => {
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)

    try {
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/GetTSbyWeek`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userID: 0,
          startDate: toISODate(monday),
          endDate: toISODate(friday),
        }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data: any[] = await res.json()

      if (data.length > 0) {
        const firstEntry = data[0]
        const weekTimesheetId = firstEntry.timesheetID ?? firstEntry.TimesheetID
        if (weekTimesheetId) {
          setSelectedWeekTimesheetId(weekTimesheetId)
          setTimesheetId(weekTimesheetId)
        }
        const statusVal = firstEntry.status ?? firstEntry.Status ?? null
        setTimesheetStatus(typeof statusVal === "number" ? statusVal : null)
      } else {
        setTimesheetStatus(null)
        setTimesheetId(null)
        setSelectedWeekTimesheetId(null)
      }

      const mapped: TimesheetEntry[] = data.map((d) => {
        const dateObj = new Date(d.date)
        const weekday = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dateObj.getDay()]
        const monthName = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][dateObj.getMonth()]
        const label = `${weekday}, ${monthName} ${dateObj.getDate()}`
        return {
          lineID: d.lineID ?? d.LineID ?? 0,
          id: String(d.lineID ?? d.LineID ?? `${d.engagementID}-${d.taskID}-${d.date}`),
          engagement: d.engagementName ?? d.EngagementName ?? "",
          task: d.taskName ?? d.TaskName ?? "",
          hours: d.hours ?? d.Hours ?? 0,
          minutes: d.minutes ?? d.Minutes ?? 0,
          comments: decodeHtmlEntities(d.comment ?? d.Comment ?? ""),
          date: label,
        }
      })
      setEntries(mapped)
      setIsViewingSpecificWeek(true)
    } catch (e) {
      console.error("Failed to load week entries", e)
      setEntries([])
    }
  }

  const reloadCurrentData = async () => {
    if (isViewingSpecificWeek) {
      try {
        const [range, yearStr] = currentWeek.split(", ")
        const [startStr] = range.split(" - ")
        const [monAbbr, dayStr] = startStr.split(" ")
        const mm: { [k: string]: number } = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
        const monday = new Date(Number.parseInt(yearStr), mm[monAbbr], Number.parseInt(dayStr))
        await loadWeekEntries(monday)
      } catch (e) {
        console.error("Failed to reload specific week", e)
        await loadCurrentTimesheet()
      }
    } else {
      await loadCurrentTimesheet()
    }
  }

  const loadCurrentTimesheet = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/v1`, { credentials: "include" })
      if (!res.ok) throw new Error(`${res.status}`)
      const payload = await res.json()
      const data = payload?.data
      const lines: any[] = data?.timesheetLines || []

      if (data?.currentWeekStartDate && data?.currentWeekEndDate) {
        const start = new Date(data.currentWeekStartDate)
        const end = new Date(data.currentWeekEndDate)
        const toISO = (d: Date) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`
        setV1CurrentStartISO(toISO(start))
        setV1CurrentEndISO(toISO(end))
      }
      setRejectedCount(data?.totalRejectedCount ?? 0)
      setRejectedRanges(Array.isArray(data?.rejectedTimesheetRanges) ? data.rejectedTimesheetRanges : [])
      
      if (data?.startDate && data?.endDate) {
        const start = new Date(data.startDate)
        const end = new Date(data.endDate)
        const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
        const wk = `${monthsAbbr[start.getMonth()]} ${start.getDate().toString().padStart(2,'0')} - ${monthsAbbr[end.getMonth()]} ${end.getDate().toString().padStart(2,'0')}, ${start.getFullYear()}`
        setCurrentWeek(wk)
        updateWeekDays(wk, true)  // Pass true to preserve selected date
      }
      
      if (data?.timesheetID) setTimesheetId(data.timesheetID)
      if (typeof data?.status === 'number') setTimesheetStatus(data.status)
      setIsViewingSpecificWeek(false)
      
      const mapped: TimesheetEntry[] = lines.map((d) => {
        const dateObj = new Date(d.date)
        const weekday = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dateObj.getDay()]
        const monthName = ["January","February","March","April","May","June","July","August","September","October","November","December"][dateObj.getMonth()]
        const label = `${weekday}, ${monthName} ${dateObj.getDate()}`
        return {
          lineID: d.lineID ?? d.LineID ?? 0,
          id: String(d.lineID ?? d.LineID ?? `${d.engagementID}-${d.taskID}-${d.date}`),
          engagement: d.engagementName ?? d.EngagementName ?? "",
          task: d.taskName ?? d.TaskName ?? "",
          hours: d.hours ?? d.Hours ?? 0,
          minutes: d.minutes ?? d.Minutes ?? 0,
          comments: decodeHtmlEntities(d.comment ?? d.Comment ?? ""),
          date: label,
        }
      })
      setEntries(mapped)
    } catch (e) {
      console.error("Failed to load current timesheet", e)
      setEntries([])
    }
  }

  const handleSaveEntry = async () => {
    if (isSaving) return
    if (newEntry.minutes % 5 !== 0) {
      setWarnMessage("Minutes must be in multiples of 5 (0,5,10,...,55)")
      setShowWarnToast(true)
      setTimeout(()=>setShowWarnToast(false),3000)
      return
    }
    if (newEntry.hours > 12 || (newEntry.hours === 12 && newEntry.minutes > 0)) {
      setWarnMessage("Time cannot exceed 12 hours")
      setShowWarnToast(true)
      setTimeout(()=>setShowWarnToast(false),3000)
      return
    }
    
    const existingMinutes = getDayTotalsMinutes(selectedDate)
    const newEntryMinutes = (newEntry.hours * 60) + newEntry.minutes
    const dayTotalMinutes = existingMinutes + newEntryMinutes
    const dayTotalHours = Math.floor(dayTotalMinutes / 60)
    const dayTotalMinutesRemainder = dayTotalMinutes % 60
    
    if (dayTotalHours > 12 || (dayTotalHours === 12 && dayTotalMinutesRemainder > 0)) {
      setWarnMessage("Daily total hours cannot exceed 12 hours")
      setShowWarnToast(true)
      setTimeout(()=>setShowWarnToast(false),3000)
      return
    }
    const pickedEng = engagementOptions.find(e => e.title === newEntry.engagement)
    const pickedTask = taskOptions.find(t => t.taskName === newEntry.task)
    const day = weekDays.find(d => d.full === selectedDate)
    if (!pickedEng || !pickedTask || !day) return

    const baseMinutes = getDayTotalsMinutes(selectedDate)
    const newMinutes = (newEntry.hours * 60) + newEntry.minutes
    const dayTotal = baseMinutes + newMinutes
    const totalDayHours = Math.floor(dayTotal / 60)
    const totalDayMinutes = dayTotal % 60

    setIsSaving(true)
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          lineID: null,
          timesheetID: timesheetId ?? 0,
          engagementID: pickedEng.engagementID,
          taskID: pickedTask.taskID,
          hours: newEntry.hours,
          minutes: newEntry.minutes,
          date: `${day.iso}T00:00:00`,
          comment: newEntry.comments,
          totalDayHours,
          totalDayMinutes,
          modUser: 0,
        })
      })

      if (!res.ok) throw new Error(`${res.status}`)
      await reloadCurrentData()
      if (!isViewingSpecificWeek) {
        await loadCurrentTimesheet()
      }
      setNewEntry(createEmptyEntry())
      setIsAddingEntry(false)
    } catch (e) {
      console.error("Failed to save entry", e)
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddEntry = () => {
    if (editingEntry) { 
      setWarnMessage("Add/Edit cannot be done at the same time");
      setShowWarnToast(true);
      setTimeout(()=>setShowWarnToast(false),3000);
      return;
    }
  
    const errors = { engagement: "", task: "", hours: "" };
    let hasError = false;
  
    if (!newEntry.engagement) {
      errors.engagement = "Please add engagement";
      hasError = true;
    }
  
    if (!newEntry.task) {
      errors.task = "Please add task";
      hasError = true;
    }
  
    setAddEntryErrors(errors);
  
    if (hasError) return;
  
    handleSaveEntry();
  };
  

  useEffect(() => {
    if (!isAddingEntry && !editingEntry) return
    const targetDateLabel = editingEntry
      ? (entries.find(e => e.id === editingEntry)?.date ?? selectedDate)
      : selectedDate
    const day = weekDays.find((d) => d.full === targetDateLabel)
    if (!day) return
    const iso = day.iso

    fetch(process.env.REACT_APP_API_URL + `/api/Engagement/GetEngagementsByUserAndDate/${iso}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((list: any[]) => {
        setEngagementOptions(list.map(x => ({ engagementID: x.engagementID, title: x.title ?? String(x.engagementID) })))
      })
      .catch(() => setEngagementOptions([]))
  }, [isAddingEntry, editingEntry])

  const prevSelectedDateRef = useRef(selectedDate)

  useEffect(() => {
    if (prevSelectedDateRef.current !== selectedDate) {
      if (isAddingEntry) {
        setIsAddingEntry(false)
        setNewEntry(createEmptyEntry())
      }
      prevSelectedDateRef.current = selectedDate
    }
  }, [selectedDate, isAddingEntry])

  useEffect(() => {
    const selectedEngagementTitle = editingEntry ? editEntryDraft.engagement : newEntry.engagement
    if (!selectedEngagementTitle) { setTaskOptions([]); return }
    const picked = engagementOptions.find(e => e.title === selectedEngagementTitle)
    if (!picked) { setTaskOptions([]); return }
    fetch(process.env.REACT_APP_API_URL + `/api/Engagement/${picked.engagementID}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((list: any[]) => setTaskOptions(list.map(t => ({ taskID: t.taskID, taskName: t.taskName ?? String(t.taskID) }))))
      .catch(() => setTaskOptions([]))
  }, [newEntry.engagement, editEntryDraft.engagement, editingEntry, engagementOptions])

  const handleEditEntry = (entry: TimesheetEntry) => {
    if (isAddingEntry) { 
      setWarnMessage("Add/Edit cannot be done at the same time")
      setShowWarnToast(true)
      setTimeout(()=>setShowWarnToast(false),3000)
      return 
    }
    setEditEntryDraft({
      engagement: entry.engagement,
      task: entry.task,
      hours: entry.hours,
      minutes: entry.minutes,
      comments: entry.comments,
    })
    setEditingEntry(entry.id)
    setOriginalEntry(entry)
  }

  const handleUpdateEntry = async () => {
    if (isSaving) return
    if (!editingEntry || !editEntryDraft.engagement || !editEntryDraft.task) return
    if (editEntryDraft.minutes % 5 !== 0) {
      setWarnMessage("Minutes must be in multiples of 5 (0,5,10,...,55)")
      setShowWarnToast(true)
      setTimeout(()=>setShowWarnToast(false),3000)
      return
    }
    if (editEntryDraft.hours > 12 || (editEntryDraft.hours === 12 && editEntryDraft.minutes > 0)) {
      setWarnMessage("Time cannot exceed 12 hours")
      setShowWarnToast(true)
      setTimeout(()=>setShowWarnToast(false),3000)
      return
    }

    const currentEntry = entries.find(e => e.id === editingEntry)
    if (!currentEntry) return

    const existingMinutes = getDayTotalsMinutes(currentEntry.date) - (currentEntry.hours * 60 + currentEntry.minutes)
    const newEntryMinutes = (editEntryDraft.hours * 60) + editEntryDraft.minutes
    const dayTotalMinutes = existingMinutes + newEntryMinutes
    const dayTotalHours = Math.floor(dayTotalMinutes / 60)
    const dayTotalMinutesRemainder = dayTotalMinutes % 60
    
    if (dayTotalHours > 12 || (dayTotalHours === 12 && dayTotalMinutesRemainder > 0)) {
      setWarnMessage("Daily total hours cannot exceed 12 hours")
      setShowWarnToast(true)
      setTimeout(()=>setShowWarnToast(false),3000)
      return
    }

    const pickedEng = engagementOptions.find(e => e.title === editEntryDraft.engagement)
    const pickedTask = taskOptions.find(t => t.taskName === editEntryDraft.task)
    const day = weekDays.find(d => d.full === currentEntry.date)
    if (!pickedEng || !pickedTask || !day) return

    const baseMinutes = getDayTotalsMinutes(currentEntry.date) - (currentEntry.hours * 60 + currentEntry.minutes)
    const newMinutes = (editEntryDraft.hours * 60) + editEntryDraft.minutes
    const dayTotal = baseMinutes + newMinutes
    const totalDayHours = Math.floor(dayTotal / 60)
    const totalDayMinutes = dayTotal % 60

    setIsSaving(true)
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          lineID: currentEntry.lineID,
          timesheetID: timesheetId ?? 0,
          engagementID: pickedEng.engagementID,
          taskID: pickedTask.taskID,
          hours: editEntryDraft.hours,
          minutes: editEntryDraft.minutes,
          date: `${day.iso}T00:00:00`,
          comment: editEntryDraft.comments,
          totalDayHours,
          totalDayMinutes,
          modUser: 0,
        })
      })

      if (!res.ok) throw new Error(`${res.status}`)
      
      await reloadCurrentData()
      if (!isViewingSpecificWeek) {
        await loadCurrentTimesheet()
      }
      setEditEntryDraft(createEmptyEntry())
      setEditingEntry(null)
      setOriginalEntry(null)
    } catch (e) {
      console.error("Failed to update entry", e)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteEntry = async (id: string) => {
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/${id}`, {
        method: "DELETE",
        credentials: "include"
      })
      
      if (!res.ok) throw new Error(`${res.status}`)
      
      await reloadCurrentData()
    } catch (e) {
      console.error("Failed to delete entry", e)
    }
  }

  const canFillDay = (day: any) => {
    if (day.status === "future") return false
    if (day.isWeekend) {
      return weekdaysFilled || getDayHours(day.full) > 0
    }
    return true
  }

  const getDayStatus = (day: any) => {
    const dayHours = getDayHours(day.full)
    if (day.isWeekend && dayHours === 0) return null
    return dayHours >= 8 ? "complete" : "incomplete"
  }

  const getDayColor = (day: any) => {
    if (selectedDate === day.full) return "bg-blue-100 text-blue-800"

    const dayHours = getDayHours(day.full)
    if (!day.isWeekend && dayHours >= 8)
      return "bg-green-50 text-green-800 hover:bg-green-100"
    if (day.isWeekend && dayHours > 0)
      return "bg-green-50 text-green-800 hover:bg-green-100"
    if (day.status === "future")
      return "bg-gray-100 text-gray-400 cursor-not-allowed"
    if (day.isWeekend)
      return "bg-gray-100 text-gray-400"

    return "bg-gray-100 text-gray-600 hover:bg-gray-200"
  }

  const handleSubmitTimesheet = () => {
    const anyWeekdayUnderMin = weekDays
      .filter((d) => !d.isWeekend)
      .some((d) => getDayHours(d.full) < 8)
    if (anyWeekdayUnderMin) {
      setWarnMessage("Please fill min daily hours.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
      return
    }
    setIsSubmitOpen(true)
  }

  const doSubmitTimesheet = async () => {
    try {
      const totalWeekMin = getWeekTotalsMinutes()
      const hoursTotal = Math.floor(totalWeekMin / 60)
      const minutesTotal = totalWeekMin % 60
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/Submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          timesheetID: timesheetId ?? 0,
          userID: 0,
          hoursTotal,
          minutesTotal,
          submissionComment: submitComment,
        })
      })
      if (!res.ok) throw new Error(`${res.status}`)
      
      setIsSubmitOpen(false)
      setSubmitComment("")
      setShowSuccessToast(true)
      setTimeout(() => setShowSuccessToast(false), 3000)

      await new Promise(r => setTimeout(r, 800))
      try {
        const v1Res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/v1`, { credentials: "include" })
        if (v1Res.ok) {
          const p = await v1Res.json()
          const d = p?.data
          if (d?.currentWeekStartDate && d?.currentWeekEndDate) {
            const monday = new Date(d.currentWeekStartDate)
            const friday = new Date(d.currentWeekEndDate)
            await loadWeekEntries(monday)
            const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
            const wk = `${monthsAbbr[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${monthsAbbr[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
            setCurrentWeek(wk)
            updateWeekDays(wk)
            setIsViewingSpecificWeek(true)
          } else {
            setIsViewingSpecificWeek(false)
            await loadCurrentTimesheet()
          }
        } else {
          setIsViewingSpecificWeek(false)
          await loadCurrentTimesheet()
        }
      } catch {
        setIsViewingSpecificWeek(false)
        await loadCurrentTimesheet()
      }
      await loadCurrentTimesheet()
    } catch (e) {
      console.error("Failed to submit timesheet", e)
    }
  }

  const handleBulkSubmit = (bulkEntries: any[], selectedDays: string[]) => {
    const newEntries: TimesheetEntry[] = []

    selectedDays.forEach((dayKey) => {
      const dayData = weekDays.find((d) => d.day === dayKey)
      if (dayData) {
        bulkEntries.forEach((bulkEntry) => {
          newEntries.push({
            lineID: 0,
            id: `${Date.now()}-${Math.random()}`,
            engagement: bulkEntry.engagement,
            task: bulkEntry.task,
            hours: bulkEntry.hours,
            minutes: bulkEntry.minutes,
            comments: bulkEntry.comments,
            date: dayData.full,
          })
        })
      }
    })

    setEntries([...entries, ...newEntries])
  }

  useEffect(() => {
    loadCurrentTimesheet()
  }, [])

  const getLimitedText = (value: string) => {
    if (!sidebarOpen) return value
    if (value.length <= TEXT_TRUNCATE_LIMIT) return value
    return `${value.slice(0, TEXT_TRUNCATE_LIMIT)}…`
  }

  const shouldShowInfoTooltip = (value: string) =>
    Boolean(sidebarOpen && value.length > TEXT_TRUNCATE_LIMIT)

  const renderInfoCell = (value: string, label: string, baseClass: string) => {
    const hasValue = value?.trim().length > 0
    const textToRender = hasValue ? getLimitedText(value) : "-"
    const showTooltip = hasValue && shouldShowInfoTooltip(value)
    const content = (
      <div className={`flex items-center ${baseClass} ${sidebarOpen ? "truncate" : ""} ${showTooltip ? "cursor-default" : ""}`}>
        {textToRender}
      </div>
    )

    if (!showTooltip) {
      return content
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs bg-gray-900 text-white">
          <p className="text-sm font-semibold">Do you know?</p>
          <p className="text-sm">{`${label}: ${value}`}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={`p-3 sm:p-6 space-y-4 sm:space-y-6 text-gray-900 bg-gray-50`}>
        {/* Nags Section */}
        <div className="space-y-2">
          {showNag1 && rejectedCount > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-red-100 border border-red-300 rounded-lg p-3 gap-2">
              <div className="text-sm text-red-800 pr-2">
                {rejectedCount > 0 ? (
                  <span>
                    You have {rejectedCount} rejected timesheets:
                    {rejectedRanges.map((r, idx) => {
                      const s = new Date(r.startDate)
                      const e = new Date(r.endDate)
                      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                      const label = `${months[s.getMonth()]} '${String(s.getFullYear()).slice(-2)} (${s.getDate()}-${e.getDate()})`
                      return (
                        <button
                          key={r.timesheetID}
                          className="underline text-blue-700 hover:text-blue-900 ml-1"
                          onClick={async () => {
                            try {
                              const monday = new Date(r.startDate)
                              const friday = new Date(r.endDate)
                              await loadWeekEntries(monday)
                              const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                              const wk = `${monthsAbbr[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${monthsAbbr[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
                              setCurrentWeek(wk)
                              updateWeekDays(wk)
                            } catch {}
                          }}
                        >
                          {label}{idx < rejectedRanges.length - 1 ? ',' : ''}
                        </button>
                      )
                    })}
                  </span>
                ) : (
                  <span>No rejected timesheets.</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <X
                  className="h-4 w-4 cursor-pointer text-gray-500"
                  onClick={() => setShowNag1(false)}
                />
              </div>
            </div>
          )}

          {showNag2 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-yellow-100 border border-yellow-300 rounded-lg p-3 gap-2">
              <span className="text-sm text-yellow-800 pr-2">
                Notify Manger For Pending Timesheet Approval Feature. Coming Soon........
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" className="bg-transparent text-xs sm:text-sm">
                  Notify
                </Button>
                <X
                  className="h-4 w-4 cursor-pointer text-gray-500"
                  onClick={() => setShowNag2(false)}
                />
              </div>
            </div>
          )}

          {showPopup && (
            <div
              style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                backgroundColor: "#708090",
                color: "white",
                padding: "10px 20px",
                borderRadius: "8px",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                zIndex: 1000,
                height: "50px",
                width: "240px",
                textAlign: "center",
              }}
            >
              Notified Successfully
            </div>
          )}
        </div>

        {/* Week Navigation */}
        <Card className={`bg-white border-gray-200`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <h1 className={`text-lg sm:text-xl font-semibold text-gray-800`}>Add/View Timesheet</h1>
            </div>

            <Collapsible open={isGuidelinesOpen} onOpenChange={setIsGuidelinesOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 p-0 h-auto text-blue-600 hover:text-blue-700 mb-4"
                >
                  <Info className="w-4 h-4" />
                  <span className="font-medium text-sm sm:text-base">Timesheet Guidelines</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isGuidelinesOpen ? "rotate-180" : ""}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mb-4">
                <div className={`p-3 sm:p-4 rounded-lg border bg-blue-50 border-blue-200 text-blue-800`}>
                  <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                    <li>• Working days: Monday to Friday (5 days per week)</li>
                    <li>• Minimum working hours: 8 hours per day, 40 hours per week</li>
                    <li>• Saturday and Sunday are optional working days</li>
                    <li>• Weekend entries are only available after completing all weekday timesheets</li>
                    <li>• Fill previous week timesheets before accessing future weeks</li>
                    <li>• All entries must include engagement, task, and time details</li>
                  </ul>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <span className={`text-sm font-medium text-gray-600`}>Week:</span>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    className="font-medium bg-white text-sm"
                    onClick={() => setIsWeekSelectorOpen(true)}
                  >
                    {currentWeek}
                    <Calendar className="w-4 h-4 ml-2" />
                  </Button>
                  <Button 
                    variant="outline"
                    disabled={(() => {
                      if (!v1CurrentStartISO || !v1CurrentEndISO) return false
                      try {
                        const [range, yearStr] = currentWeek.split(", ")
                        const [startStr, endStr] = range.split(" - ")
                        const monthsAbbr: Record<string, number> = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
                        const parsePart = (p: string) => { const [m, d] = p.split(" "); return { m: monthsAbbr[m], d: parseInt(d,10) } }
                        const sPart = parsePart(startStr)
                        const yr = parseInt(yearStr,10)
                        const sDate = new Date(yr, sPart.m, sPart.d)
                        const toISO = (d: Date) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`
                        const currentStartISO = toISO(sDate)
                        const isCurrentWeek = currentStartISO === v1CurrentStartISO
                        return isCurrentWeek
                      } catch (e) { 
                        return false 
                      }
                    })()}
                    className="font-medium bg-white text-sm"
                    onClick={async () => {
                      try {
                        if (!v1CurrentStartISO || !v1CurrentEndISO) return
                        const monday = new Date(v1CurrentStartISO)
                        const friday = new Date(v1CurrentEndISO)
                        await loadWeekEntries(monday)
                        const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                        const wk = `${monthsAbbr[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${monthsAbbr[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
                        setCurrentWeek(wk)
                        updateWeekDays(wk)
                      } catch (e) {
                        setWarnMessage("Failed to load current timesheet")
                        setShowWarnToast(true)
                        setTimeout(()=>setShowWarnToast(false),3000)
                      }
                    }}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Go To Current TS
                  </Button>
                  {timesheetStatus !== null && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        timesheetStatus === 4 
                          ? "bg-red-100 text-red-700 border-red-300"
                          : timesheetStatus === 3
                          ? "bg-green-100 text-green-700 border-green-300"
                          : timesheetStatus === 2
                          ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                          : "bg-gray-100 text-gray-700 border-gray-300"
                      }`}
                    >
                      {timesheetStatus === 4 ? "Rejected" : 
                       timesheetStatus === 3 ? "Approved" : 
                       timesheetStatus === 2 ? "Submitted" : "Pending"}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-sm">
                  <span className={"text-gray-600"}>Weekly Total: </span>
                  <span className="font-semibold text-blue-600">{formatWeeklyHours(weekTotalHours)}</span>
                  <span className={`ml-2 text-gray-500 hidden sm:inline`}>Target: 40h</span>
                  <div className={`sm:hidden text-gray-500 text-xs`}>Target: 40h</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {weekDays.map((day) => (
                <button
                  key={day.day}
                  onClick={() => {
                    if (canFillDay(day)) {
                      setSelectedDate(day.full)
                    }
                  }}
                  className={`p-2 sm:p-3 rounded-lg text-center transition-colors relative ${getDayColor(day)}`}
                  disabled={!canFillDay(day)}
                >
                  <div className="text-xs font-medium">{day.day}</div>
                  <div className="text-base sm:text-lg font-bold">{day.date}</div>
                  {day.isWeekend && <div className="text-xs text-gray-500 hidden sm:block">Optional</div>}
                  {!day.isWeekend && <div className="text-xs mt-1 font-medium">{getDayHours(day.full).toFixed(1)}h</div>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Selected Day Details */}
        <Card className={`bg-white border-gray-200`}>
          <CardHeader className="pb-3 px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <CardTitle className={`text-base sm:text-lg text-gray-900`}>{selectedDate}</CardTitle>
                <div className="flex items-center gap-2">
                  {getDayStatus(weekDays.find((d) => d.full === selectedDate)) && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${getDayStatus(weekDays.find((d) => d.full === selectedDate)) === "complete"
                        ? "bg-green-100 text-green-700 border-green-300"
                        : "bg-orange-100 text-orange-700 border-orange-300 "
                        }`}
                    >
                      {getDayStatus(weekDays.find((d) => d.full === selectedDate)) === "complete"
                        ? "Complete"
                        : "Incomplete"}
                    </Badge>
                  )}
                  <span className={`text-sm text-gray-600`}>
                    {totalHours.toFixed(1)}h
                  </span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={() => setIsAddingEntry(true)}
                  disabled={timesheetStatus === 2 || timesheetStatus === 3}
                  className="bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Entry
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsBulkModalOpen(true)}
                  disabled
                  className="bg-white text-sm opacity-50 cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Bulk Entry
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="space-y-4">
              <div className="overflow-x-auto">
                {/* Fixed width table header */}
                <div className="min-w-[1100px]">
                  <div className="grid gap-3 text-sm font-medium border-b pb-2 text-gray-600 border-gray-200"
                       style={{ gridTemplateColumns: '2fr 1.5fr 80px 80px 1.5fr 140px' }}>
                    <div className="flex items-center">ENGAGEMENT</div>
                    <div className="flex items-center">TASK</div>
                    <div className="text-center flex items-center justify-center">HOURS</div>
                    <div className="text-center flex items-center justify-center">MINUTES</div>
                    <div className="flex items-center">COMMENTS</div>
                    <div className="flex items-center justify-end">ACTIONS</div>
                  </div>

                  {/* Add Entry Form */}
                  {isAddingEntry && (
                    <div className="border rounded-lg border-blue-200 bg-blue-50 p-4 mt-3 relative z-50">
                      <div className="grid gap-3 items-start"
                           style={{ gridTemplateColumns: '2fr 1.5fr 80px 80px 1.5fr 140px' }}>
                        <div>
                        <Select
    value={newEntry.engagement}
    onValueChange={(value) => {
      setNewEntry({ ...newEntry, engagement: value, task: "" });
      setAddEntryErrors({ ...addEntryErrors, engagement: "" });
    }}
  >
                            <SelectTrigger className="bg-white w-full">
                              <SelectValue placeholder="Select Engagement" />
                            </SelectTrigger>
                            <SelectContent className="bg-white max-h-60 overflow-y-auto z-[60]">
                              {engagementOptions.map((e) => (
                                <SelectItem key={e.engagementID} value={e.title} className="hover:bg-gray-100">
                                  {e.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {addEntryErrors.engagement && (
    <p className="text-red-600 text-xs mt-1">{addEntryErrors.engagement}</p>
  )}
                        </div>
                        <div>
                        <Select
    value={newEntry.task}
    onValueChange={(value) => {
      setNewEntry({ ...newEntry, task: value });
      setAddEntryErrors({ ...addEntryErrors, task: "" });
    }}
  >
                            <SelectTrigger className="bg-white w-full">
                              <SelectValue placeholder="Select Task" />
                            </SelectTrigger>
                            <SelectContent className="bg-white max-h-60 overflow-y-auto z-[60]">
                              {taskOptions.map((t) => (
                                <SelectItem key={t.taskID} value={t.taskName} className="hover:bg-gray-100">
                                  {t.taskName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {addEntryErrors.task && (
    <p className="text-red-600 text-xs mt-1">{addEntryErrors.task}</p>
  )}
                        </div>
                        <div>
                          <Input
                            type="number"
                            min="0"
                            max="12"
                            placeholder="0"
                            value={newEntry.hours === 0 ? "" : newEntry.hours}
                            onChange={(e) => setNewEntry({ ...newEntry, hours: Number.parseInt(e.target.value) || 0 })}
                            className="bg-white w-full text-center"
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            min="0"
                            max="59"
                            step="5"
                            placeholder="0"
                            value={newEntry.minutes === 0 ? "" : newEntry.minutes}
                            onChange={(e) => setNewEntry({ ...newEntry, minutes: Number.parseInt(e.target.value) || 0 })}
                            className="bg-white w-full text-center"
                          />
                        </div>
                        <div>
                          <Textarea
                            placeholder="Add comments..."
                            value={newEntry.comments}
                            onChange={(e) => setNewEntry({ ...newEntry, comments: e.target.value })}
                            className="min-h-[40px] bg-white resize-none"
                            rows={2}
                          />
                        </div>
                        <div className="flex gap-2 relative z-50">
                          <Button
                            size="sm"
                            onClick={handleAddEntry}
                            className="bg-green-600 hover:bg-green-700 flex-1 relative z-50"
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setIsAddingEntry(false)
                              setEditingEntry(null)
                              setOriginalEntry(null)
                              setNewEntry(createEmptyEntry())
                            }}
                            className="bg-white flex-1 relative z-50"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Entries List */}
                  {currentDayEntries.length === 0 ? (
                    <div className={`text-center py-8 text-gray-500`}>
                      <Clock className={`w-12 h-12 mx-auto mb-3 text-gray-300`} />
                      <p>No entries for this day</p>
                      <p className="text-sm">Click "Add Entry" to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-2 mt-3">
                      {currentDayEntries.map((entry) => (
                        <div key={entry.id} className="border-b last:border-b-0 border-gray-100 py-3 relative">
                          {editingEntry === entry.id ? (
                            <div className="grid gap-3 items-start relative z-50"
                                 style={{ gridTemplateColumns: '2fr 1.5fr 80px 80px 1.5fr 140px' }}>
                              <div>
                                <Select
                                  value={editEntryDraft.engagement}
                                  onValueChange={(value) => setEditEntryDraft({ ...editEntryDraft, engagement: value, task: "" })}
                                >
                                  <SelectTrigger className="bg-white">
                                    <SelectValue placeholder="Select Engagement" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white max-h-60 overflow-y-auto">
                                    {engagementOptions.map((e) => (
                                      <SelectItem key={e.engagementID} value={e.title} className="hover:bg-gray-100">
                                        {e.title}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Select value={editEntryDraft.task} onValueChange={(value) => setEditEntryDraft({ ...editEntryDraft, task: value })}>
                                  <SelectTrigger className="bg-white">
                                    <SelectValue placeholder="Select Task" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white max-h-60 overflow-y-auto">
                                    {taskOptions.map((t) => (
                                      <SelectItem key={t.taskID} value={t.taskName} className="hover:bg-gray-100">
                                        {t.taskName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Input
                                  type="number"
                                  min="0"
                                  max="12"
                                  placeholder="0"
                                  value={editEntryDraft.hours === 0 ? "" : editEntryDraft.hours}
                                  onChange={(e) => setEditEntryDraft({ ...editEntryDraft, hours: Number.parseInt(e.target.value) || 0 })}
                                  className="bg-white text-center"
                                />
                              </div>
                              <div>
                                <Input
                                  type="number"
                                  min="0"
                                  max="59"
                                  step="5"
                                  placeholder="0"
                                  value={editEntryDraft.minutes === 0 ? "" : editEntryDraft.minutes}
                                  onChange={(e) => setEditEntryDraft({ ...editEntryDraft, minutes: Number.parseInt(e.target.value) || 0 })}
                                  className="bg-white text-center"
                                />
                              </div>
                              <div>
                                <Textarea
                                  placeholder="Add comments..."
                                  value={editEntryDraft.comments}
                                  onChange={(e) => setEditEntryDraft({ ...editEntryDraft, comments: e.target.value })}
                                  className="min-h-[40px] bg-white resize-none"
                                  rows={2}
                                />
                              </div>
                              <div className="flex gap-2 relative z-50">
                                <Button size="sm" onClick={handleUpdateEntry} className="bg-green-600 hover:bg-green-700 flex-1 relative z-50">
                                  Update
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingEntry(null)
                                    setOriginalEntry(null)
                                    setEditEntryDraft(createEmptyEntry())
                                  }}
                                  className="bg-white flex-1 relative z-50"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="grid gap-3 items-center"
                                 style={{ gridTemplateColumns: '2fr 1.5fr 80px 80px 1.5fr 140px' }}>
                              {renderInfoCell(entry.engagement, "Engagement", "font-medium text-gray-800")}

                              {renderInfoCell(entry.task, "Task", "text-gray-600")}

                              <div className="text-center font-medium text-gray-900 flex items-center justify-center">{entry.hours}</div>
                              <div className="text-center font-medium text-gray-900 flex items-center justify-center">{entry.minutes.toString().padStart(2, "0")}</div>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center text-gray-600 truncate cursor-default">
                                    {entry.comments || "-"}
                                  </div>
                                </TooltipTrigger>
                                {entry.comments && (
                                  <TooltipContent side="top" className="max-w-xs bg-gray-900 text-white">
                                    <p className="text-sm whitespace-pre-wrap">{entry.comments}</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>

                              <div className="flex gap-2 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditEntry(entry)}
                                  disabled={timesheetStatus === 2 || timesheetStatus === 3}
                                  className="text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                  disabled={timesheetStatus === 2 || timesheetStatus === 3}
                                  className="text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Section */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-sm">
                  <span className={"text-gray-600"}>
                    Weekly Total: <strong>{formatWeeklyHours(weekTotalHours)}</strong> / 40h required
                  </span>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    disabled={timesheetStatus === 1}
                    onClick={async () => {
                      if (!timesheetId) { setCommentsData([]); setIsCommentsOpen(true); return }
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/getvptimesheetcomments?timesheetid=${timesheetId}`, { credentials: "include" })
                        if (res.ok) {
                          const list = await res.json()
                          setCommentsData(Array.isArray(list) ? list : [])
                        } else {
                          setCommentsData([])
                        }
                      } catch {
                        setCommentsData([])
                      }
                      setIsCommentsOpen(true)
                    }}
                    className="flex-1 sm:flex-none"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Comments
                  </Button>
                  <Button
                    onClick={handleSubmitTimesheet}
                    disabled={weekTotalHours < 40 || timesheetStatus === 2 || timesheetStatus === 3}
                    className={`flex-1 sm:flex-none ${weekTotalHours >= 40
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-gray-400 cursor-not-allowed"
                      }`}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modals */}
        <Dialog open={isSubmitOpen} onOpenChange={setIsSubmitOpen}>
          <DialogContent className={`max-w-sm bg-white`}>
            <DialogHeader>
              <DialogTitle className={'text-gray-900'}>Submit Confirmation</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">Are you sure you want to submit?</p>
              <div>
                <div className="flex items-center justify-between mb-1">
                <label className="block text-sm text-gray-600">Comments</label>
                  <span className="text-xs text-gray-500">
                  {submitComment.length}/1000
                </span>
              </div>
              <Textarea 
                value={submitComment} 
                onChange={(e) => setSubmitComment(e.target.value)} 
                placeholder="Optional comments"
                maxLength={1000}
              />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsSubmitOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={doSubmitTimesheet} className="flex-1 bg-blue-600 hover:bg-blue-700">Submit</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {(showSuccessToast || showWarnToast) && (
          <div
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              backgroundColor: "white",
              color: showWarnToast ? "#92400e" : "black",
              padding: "12px 20px",
              borderRadius: "8px",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              zIndex: 1000,
              border: showWarnToast ? "2px solid #fbbf24" : "2px solid #10b981",
            }}
          >
            {showWarnToast ? warnMessage : "Timesheet Submitted Successfully"}
          </div>
        )}

        <BulkEntryModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          onSubmit={handleBulkSubmit}
        />

        <WeekSelector
          isOpen={isWeekSelectorOpen}
          onClose={() => setIsWeekSelectorOpen(false)}
          currentWeek={currentWeek}
          rejectedRanges={rejectedRanges}
          selectedDate={selectedDate}
          maxSelectableISO={v1CurrentEndISO ?? undefined}
          onWeekSelect={(week) => {
            setCurrentWeek(week)
            try {
              const [range, yearStr] = week.split(", ")
              const [startStr] = range.split(" - ")
              const [monAbbr, dayStr] = startStr.split(" ")
              const mm: { [k: string]: number } = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
              const monday = new Date(Number.parseInt(yearStr), mm[monAbbr], Number.parseInt(dayStr))
              loadWeekEntries(monday)
            } catch (e) {
              console.error("Failed to parse week label", e)
            }
            setIsWeekSelectorOpen(false)
          }}
        />

        <CommentsModalEmp
          isOpen={isCommentsOpen}
          onClose={() => setIsCommentsOpen(false)}
          title={`Timesheet #${timesheetId ?? ''}`}
          comments={
            commentsData.map((c: any, idx: number) => ({
              id: idx + 1,
              user: {
                name: c.commentByUser ?? "",
                initials: (c.commentByUser?.split(' ').map((p: string) => p[0]).join('') ?? 'U').slice(0,2).toUpperCase(),
              },
              action: c.commentTypeText ?? "",
              timestamp: new Date(c.commentDate).toLocaleString(),
              commentText: c.commentText && c.commentText.trim().length > 0 ? c.commentText : undefined,
            }))
          }
        />
      </div>
    </TooltipProvider>
  )
}